import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  Typography,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  CardContent,
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

import Swal from "sweetalert2";
import { auth, db } from "../firebase";
import {
  collection,
  addDoc,
  query,
  where,
  onSnapshot,
  deleteDoc,
  doc,
  updateDoc,
  getDoc,
  Timestamp,
} from "firebase/firestore";

export default function ProductCategory() {
  /* ================= TOAST ================= */
  const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 3000,
  });

  /* ================= STATE ================= */
  const [user, setUser] = useState(null);
  const [propertyId, setPropertyId] = useState("");
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");

  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);

  const [catname, setCatname] = useState("");
  const [status, setStatus] = useState("YES");

  const [editId, setEditId] = useState("");
  const [editCat, setEditCat] = useState("");
  const [editStatus, setEditStatus] = useState("YES");

  const [priority, setPriority] = useState("");
  const [editPriority, setEditPriority] = useState("");

  /* ================= AUTH + PROPERTY ================= */
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (u) => {
      if (!u) return;

      setUser(u);

      const roleSnap = await getDoc(doc(db, "authroles", u.uid));
      if (!roleSnap.exists()) {
        Toast.fire({ icon: "error", title: "Property not assigned" });
        return;
      }

      setPropertyId(roleSnap.data().propertyId);
    });

    return () => unsub();
  }, []);

  /* ================= FETCH DATA ================= */
  useEffect(() => {
    if (!user || !propertyId) return;

    const q = query(
      collection(db, "productcats"),
      where("propertyId", "==", propertyId)
    );

    const unsub = onSnapshot(q, (snap) => {
      let i = 1;
      const data = snap.docs
        .map((d) => ({
          id: d.id,
          ...d.data(),
        }))
        .sort((a, b) => (a.priority || 0) - (b.priority || 0))
        .map((item, index) => ({
          ...item,
          sr: index + 1,
        }));
      setRows(data);
    });

    return () => unsub();
  }, [user, propertyId]);

  /* ================= ADD ================= */
  const handleAdd = async () => {
    if (!catname.trim()) {
      Toast.fire({ icon: "error", title: "Category name required" });
      return;
    }

    try {
      await addDoc(collection(db, "productcats"), {
        propertyId,
        catname: catname.trim(),
        priority: Number(priority) || 0,

        status,
        createdAt: Timestamp.now(),
      });

      setCatname("");
      setStatus("YES");
      setOpenAdd(false);
      setPriority("");
      Toast.fire({ icon: "success", title: "Category Added" });
    } catch (err) {
      Toast.fire({ icon: "error", title: err.message });
    }
  };

  /* ================= UPDATE ================= */
  const handleUpdate = async () => {
    if (!editId || !editCat.trim()) return;

    try {
      await updateDoc(doc(db, "productcats", editId), {
        catname: editCat.trim(),
        status: editStatus,
        priority: Number(editPriority) || 0,
        updatedAt: Timestamp.now(),
      });

      setOpenEdit(false);
      Toast.fire({ icon: "success", title: "Category Updated" });
    } catch (err) {
      Toast.fire({ icon: "error", title: err.message });
    }
  };

  /* ================= DELETE ================= */
  const handleDelete = async (id) => {
    const res = await Swal.fire({
      title: "Delete Category?",
      text: "This action cannot be undone",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
    });

    if (!res.isConfirmed) return;

    await deleteDoc(doc(db, "productcats", id));
    Toast.fire({ icon: "success", title: "Category Deleted" });
  };

  /* ================= SEARCH ================= */
  const filteredRows = rows.filter((r) =>
    r.catname?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box className=" p-4">
      <Card>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" mb={2}>
            <Typography variant="h6">Product Categories</Typography>
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              onClick={() => setOpenAdd(true)}
            >
              Add Category
            </Button>
          </Stack>

          <TextField
            size="small"
            label="Search Category"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ mb: 2 }}
          />

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={60}>Sr</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Stock Calculate</TableCell>
                  <TableCell width={80}>Priority</TableCell>
                  <TableCell width={120}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No Data Found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.sr}</TableCell>
                      <TableCell>{row.catname}</TableCell>
                      <TableCell>{row.status}</TableCell>
                      <TableCell>{row.priority}</TableCell>
                      <TableCell>
                        <IconButton
                          color="primary"
                          onClick={() => {
                            setEditId(row.id);
                            setEditCat(row.catname);
                            setEditStatus(row.status);
                            setEditPriority(row.priority || 0);
                            setOpenEdit(true);
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => handleDelete(row.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* ADD DIALOG */}
      <Dialog open={openAdd} onClose={() => setOpenAdd(false)}>
        <DialogTitle>Add Product Category</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth size="small"
            label="Category Name"
            margin="dense"
            value={catname}
            onChange={(e) => setCatname(e.target.value)}
          />
          <Select
            fullWidth size="small"
            label="Stock Calculate"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            sx={{ mt: 2 }}
          >
            <MenuItem value="YES">YES</MenuItem>
            <MenuItem value="NO">NO</MenuItem>
          </Select>

          <TextField
            fullWidth size="small"
            type="number"
            sx={{ mt: 2 }}
            label="Priority"
            margin="dense"
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAdd(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAdd}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* EDIT DIALOG */}
      <Dialog open={openEdit} onClose={() => setOpenEdit(false)}>
        <DialogTitle>Edit Product Category</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth size="small"
            label="Category Name"
            margin="dense"
            value={editCat}
            onChange={(e) => setEditCat(e.target.value)}
          />
          <Select
            fullWidth size="small"
            label="Stock Calculate"
            value={editStatus}
            onChange={(e) => setEditStatus(e.target.value)}
            sx={{ mt: 1 }}
          >
            <MenuItem value="YES">YES</MenuItem>
            <MenuItem value="NO">NO</MenuItem>
          </Select>
          
          <TextField
            fullWidth size="small"
            type="number"
            label="Priority"
            sx={{ mt: 2 }}
            margin="dense"
            value={editPriority}
            onChange={(e) => setEditPriority(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEdit(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdate}>
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
